import { makeAutoObservable } from "mobx";
import {
  LANDSCAPE_DIMENSIONS,
  PORTRAIT_DIMENSIONS,
  TEMPLATE_DEFINITIONS_LANDSCAPE,
  TEMPLATE_DEFINITIONS_PORTRAIT,
} from "./TemplateDefinitions";
import { IGlobalPeriodContext, IStructureGrouping } from "../../main";

// Define the cell interface
export interface IReportCell {
  id: string; // format: "row-col" e.g., "0-0", "1-2"
  width: number; // percentage of row width
  height: number; // percentage of container height
  chartWidth?: number;
  chartHeight?: number;
  componentId?: string; // ID of the component rendered in this cell
  position?: string;
  left?: number;
  top?: number;
  alignment?: AlignmentType;
  periodContext?: IGlobalPeriodContext;
  structureContext?: IStructureGrouping;
  extraOptions?: Record<string, any>;
}

export enum TemplateType {
  FULL_SCREEN = "FULL_SCREEN",
  HALF_SCREEN_VERTICAL = "HALF_SCREEN_VERTICAL",
  HALF_SCREEN_HORIZONTAL = "HALF_SCREEN_HORIZONTAL",
  FOUR_BY_FOUR = "FOUR_BY_FOUR",
  TOP_BOTTOM_SPLIT = "TOP_BOTTOM_SPLIT",
  BOTTOM_TOP_SPLIT = "BOTTOM_TOP_SPLIT",
  LEFT_RIGHT_SPLIT = "LEFT_RIGHT_SPLIT",
  RIGHT_LEFT_SPLIT = "RIGHT_LEFT_SPLIT",
  // Add more templates as needed
}

export enum AlignmentType {
  TOP_LEFT = "TOP_LEFT",
  TOP_RIGHT = "TOP_RIGHT",
  BOTTOM_LEFT = "BOTTOM_LEFT",
  BOTTOM_RIGHT = "BOTTOM_RIGHT",
  CENTER = "CENTER",
  CENTER_LEFT = "CENTER_LEFT",
  CENTER_RIGHT = "CENTER_RIGHT",
  CENTER_TOP = "CENTER_TOP",
  CENTER_BOTTOM = "CENTER_BOTTOM",
}

// Define the page interface
interface IReportPage {
  id: string;
  title: string;
  template: TemplateType;
  cells: { [cellId: string]: IReportCell };
}

const INITIAL_PORTRAIT_PAGES = {
  "0": {
    id: "0",
    title: "Page title",
    template: TemplateType.FULL_SCREEN,
    cells: (() => {
      const template = TemplateType.FULL_SCREEN;
      return TEMPLATE_DEFINITIONS_PORTRAIT[template].cells.reduce(
        (acc, cell) => {
          acc[cell.id] = { ...cell };
          return acc;
        },
        {} as { [cellId: string]: IReportCell }
      );
    })(),
  },
} as { [id: string]: IReportPage };

const INITIAL_LANDSCAPE_PAGES = {
  "0": {
    id: "0",
    title: "Page title",
    template: TemplateType.FULL_SCREEN,
    cells: (() => {
      const template = TemplateType.FULL_SCREEN;
      return TEMPLATE_DEFINITIONS_LANDSCAPE[template].cells.reduce(
        (acc, cell) => {
          acc[cell.id] = { ...cell };
          return acc;
        },
        {} as { [cellId: string]: IReportCell }
      );
    })(),
  },
} as { [id: string]: IReportPage };

export const ReportBespokeStore = () =>
  makeAutoObservable({
    templateDefinitions: TEMPLATE_DEFINITIONS_LANDSCAPE,
    pages: INITIAL_LANDSCAPE_PAGES,
    loading: false,
    generating: false,

    // Page operations
    addPage(
      title: string = "Page title",
      template: TemplateType = TemplateType.FULL_SCREEN
    ) {
      const pageIds = Object.keys(this.pages);
      const nextId =
        pageIds.length > 0
          ? Math.max(...pageIds.map((id) => parseInt(id))) + 1
          : 0;
      const pageId = nextId.toString();

      const templateDefinition = this.templateDefinitions[template];
      const cells = templateDefinition.cells.reduce((acc, cell) => {
        acc[cell.id] = { ...cell };
        return acc;
      }, {} as { [cellId: string]: IReportCell });

      this.pages[pageId] = {
        id: pageId,
        title,
        template,
        cells,
      };
    },

    isLandscape() {
      return (
        this.templateDefinitions.FULL_SCREEN.cells[0].chartHeight === undefined
      );
    },

    getDimensions() {
      return this.isLandscape() ? LANDSCAPE_DIMENSIONS : PORTRAIT_DIMENSIONS;
    },

    changeOrientation() {
      const currentOrientation = this.isLandscape() ? "landscape" : "portrait";
      if (currentOrientation === "portrait") {
        //setting landscape dimensions
        this.templateDefinitions = TEMPLATE_DEFINITIONS_LANDSCAPE;
        this.pages = INITIAL_LANDSCAPE_PAGES;
      } else {
        //setting portrait dimensions
        this.templateDefinitions = TEMPLATE_DEFINITIONS_PORTRAIT;
        this.pages = INITIAL_PORTRAIT_PAGES;
      }
    },

    removePage(pageId: string) {
      delete this.pages[pageId];
    },

    updatePageTitle(pageId: string, title: string) {
      if (this.pages[pageId]) {
        this.pages[pageId].title = title;
      }
    },

    updatePageTemplate(pageId: string, template: TemplateType) {
      const page = this.pages[pageId];
      if (page) {
        // Reset all existing cells' DOM state
        Object.keys(page.cells).forEach((cellId) => {
          const cell = document.querySelector(
            `[data-cell-id="${pageId}-${cellId}"]`
          ) as HTMLDivElement;
          if (cell) {
            cell.classList.remove("populated");
            cell.style.opacity = "0.7";
            cell.style.backgroundColor = "rgb(215, 221, 230)";
          }
        });

        const templateDefinition = this.templateDefinitions[template];
        page.template = template;
        // Reset cells to default template definition without preserving componentIds
        page.cells = templateDefinition.cells.reduce((acc, cell) => {
          acc[cell.id] = { ...cell };
          return acc;
        }, {} as { [cellId: string]: IReportCell });
      }
    },

    updateCellComponentId(pageId: string, cellId: string, componentId: string) {
      const page = this.pages[pageId];
      if (page && page.cells[cellId]) {
        page.cells[cellId].componentId = componentId;
      }
    },

    removeCellComponent(pageId: string, cellId: string) {
      const page = this.pages[pageId];
      if (page && page.cells[cellId]) {
        delete page.cells[cellId].componentId;
      }
    },

    setGenerating(generating: boolean) {
      this.generating = generating;
    },

    setLoading(loading: boolean) {
      this.loading = loading;
    },

    getComponentId(combinedId: string): string | undefined {
      const [pageId, ...cellParts] = combinedId.split("-");

      const cellId = cellParts.join("-");

      const page = this.pages[pageId];
      if (!page || !page.cells[cellId]) {
        return undefined;
      }
      console.log({ pageId, cellParts, cells: page.cells[cellId] });
      return page.cells[cellId].componentId;
    },

    updateCellAlignment(
      pageId: string,
      cellId: string,
      alignment: AlignmentType
    ) {
      const page = this.pages[pageId];
      if (page && page.cells[cellId]) {
        page.cells[cellId].alignment = alignment;
      }
    },

    saveReport() {
      // Create a deep copy of the pages object to ensure we're not keeping any reactive references
      const reportData = this.pages;
      const orientation = this.isLandscape() ? "landscape" : "portrait";
      console.log(
        JSON.parse(JSON.stringify({ pages: reportData, orientation }))
      );
      return JSON.parse(JSON.stringify({ pages: reportData, orientation }));
    },

    loadReport(data: string) {
      const savedData: {
        pages: { [id: string]: IReportPage };
        orientation: "portrait" | "landscape";
      } = JSON.parse(data);
      console.log({ savedData });
      const reportData = savedData.pages;
      const newTemplateDefinitions =
        savedData.orientation === "landscape"
          ? TEMPLATE_DEFINITIONS_LANDSCAPE
          : TEMPLATE_DEFINITIONS_PORTRAIT;
      this.templateDefinitions = newTemplateDefinitions;
      // Validate the report data structure
      if (!reportData || typeof reportData !== "object") {
        throw new Error("Invalid report data format");
      }

      // Reset the current pages and load the new report data
      this.pages = {};
      Object.entries(reportData).forEach(([pageId, page]) => {
        this.pages[pageId] = {
          id: page.id,
          title: page.title,
          template: page.template,
          cells: Object.entries(page.cells).reduce((acc, [cellId, cell]) => {
            acc[cellId] = { ...cell };
            return acc;
          }, {} as { [cellId: string]: IReportCell }),
        };
      });

      // Use setTimeout to ensure DOM elements are rendered before updating their state
      setTimeout(() => {
        Object.keys(this.pages).forEach((pageId) => {
          const page = this.pages[pageId];
          Object.keys(page.cells).forEach((cellId) => {
            const cell = document.querySelector(
              `[data-cell-id="${pageId}-${cellId}"]`
            ) as HTMLDivElement;
            if (cell) {
              cell.classList.add("populated");
              cell.style.opacity = "1";
              cell.style.backgroundColor = "transparent";
            }
          });
        });
      }, 0);
    },

    updateCell<K extends keyof IReportCell>(
      pageId: string,
      cellId: string,
      field: K,
      value: IReportCell[K]
    ) {
      const page = this.pages[pageId];
      if (page && page.cells[cellId]) {
        page.cells[cellId][field] = value;
      }
    },
  });

export type ReportBespokeStoreType = ReturnType<typeof ReportBespokeStore>;
